import { white } from '@kmns-internal/furikake'
import { InfoOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material'
import { ReportExportStatusEnum } from '@ulysses-inc/harami_api_client'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage'
import { useLimitedFeatureAvailability } from 'src/features/featureAvailability/useLimitedFeatureAvailability'
import { ReportExportListWrapper } from 'src/features/reportExport/list/ReportExportListWrapper'
import { useMutationDownloadFile } from 'src/features/reportExport/list/api/useMutationDownloadFile'
import {
  useQueryReportExports,
  type ReportExport,
} from 'src/features/reportExport/list/api/useQueryReportExports'
import { ReportExportRowMenu } from 'src/features/reportExport/list/components/ReportExportRowMenu'
import { StatusIcon } from 'src/features/reportExport/list/components/StatusIcon'
import { useReportExportListFilters } from 'src/features/reportExport/list/filters/useReportExportListFilters'
import { useLastDefinedValue } from 'src/hooks/useLastDefinedValue'

export const ReportExportList = () => {
  const location = useLocation<{ showSuccessMessage?: boolean }>()
  const history = useHistory()
  const [snackbarOpen, setSnackbarOpen] = useState(
    () => location.state?.showSuccessMessage ?? false,
  )

  const { filters, setFilters } = useReportExportListFilters()
  const reportExportsQuery = useQueryReportExports(filters)

  // ページ切替時にページネーターが消失するとユーザー体験が悪いため
  const reportExportsCount = useLastDefinedValue(reportExportsQuery.data?.count)

  const { mutate: downloadFile } = useMutationDownloadFile()

  const { getFeatureAvailability } = useLimitedFeatureAvailability()
  // 再出力は一括出力と同じ管理者限定の機能として提供する。
  // ひな形1件の条件は管理者以外でも新規出力で入力し直せるため、再出力は不要。
  const canReexport = getFeatureAvailability('bulkReportExport') === 'yes'

  // 過去の出力条件を新規出力フォームに引き継いで遷移する
  const handleReexport = (reportExport: ReportExport) => {
    if (!reportExport.filterCondition) return
    history.push('/reportExports/new', {
      reexportFilterCondition: reportExport.filterCondition,
      reexportFileName: reportExport.fileName,
    })
  }

  // location.state をクリアするために履歴を置き換え
  useEffect(() => {
    if (location.state?.showSuccessMessage) {
      history.replace(location.pathname, {})
    }
  }, [location.state, location.pathname, history])
  const handleChangePage = (_event: unknown, newPage: number) => {
    setFilters({ page: newPage + 1, pageSize: filters.pageSize })
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFilters({ page: 1, pageSize: parseInt(event.target.value, 10) })
  }

  if (reportExportsQuery.isError) {
    return (
      <ReportExportListWrapper>
        <ErrorMessage noMargin>エラーが発生しました</ErrorMessage>
      </ReportExportListWrapper>
    )
  }

  return (
    <>
      <ReportExportListWrapper>
        <TableContainer>
          <Table sx={{ bgcolor: white, width: '100%' }} stickyHeader>
            <TableHead sx={{ backgroundColor: white }}>
              <TableRow>
                <TableCell size="small" sx={{ backgroundColor: white }}>
                  ファイル名
                </TableCell>
                <TableCell
                  size="small"
                  sx={{ width: '130px', backgroundColor: white }}
                >
                  ステータス
                </TableCell>
                <TableCell
                  size="small"
                  sx={{ width: '200px', backgroundColor: white }}
                >
                  出力日時
                </TableCell>
                <TableCell
                  size="small"
                  sx={{ width: '200px', backgroundColor: white }}
                >
                  有効期限
                </TableCell>
                <TableCell
                  size="small"
                  sx={{ width: '220px', backgroundColor: white }}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportExportsQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Stack sx={{ justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : reportExportsQuery.data?.reportExports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Stack sx={{ justifyContent: 'center', py: 4 }}>
                      データがありません
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                reportExportsQuery.data?.reportExports.map(reportExport => {
                  const isExpired = reportExport.expiredAt < new Date()
                  return (
                    <TableRow key={reportExport.id}>
                      <TableCell
                        sx={{
                          maxWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {reportExport.fileName}
                      </TableCell>
                      <TableCell>
                        <StatusIcon
                          status={reportExport.status}
                          isPartial={reportExport.isPartial}
                        />
                      </TableCell>
                      <TableCell>
                        {format(reportExport.requestedAt, 'yyyy/MM/dd HH:mm')}
                      </TableCell>
                      <TableCell>
                        {format(reportExport.expiredAt, 'yyyy/MM/dd HH:mm')}
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          {/* 一覧は再出力のため有効期限後も残るが、ファイルは期限切れで
                              消える。期限内はダウンロード可能、期限切れは
                              ダウンロード不可であることを Disabled + tooltip で示す */}
                          {reportExport.status ===
                            ReportExportStatusEnum.COMPLETED && (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              {isExpired ? (
                                <Button variant="text" disabled>
                                  ダウンロード
                                </Button>
                              ) : (
                                <Button
                                  onClick={() =>
                                    downloadFile({
                                      reportExportId: reportExport.id,
                                    })
                                  }
                                  variant="text"
                                >
                                  ダウンロード
                                </Button>
                              )}
                              {/* ⓘ アイコン分のスペースを常に確保し、
                                  期限内/期限切れでダウンロード文言の位置を揃える */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  width: 20,
                                  justifyContent: 'center',
                                }}
                              >
                                {isExpired && (
                                  <Tooltip
                                    title={
                                      <>
                                        ダウンロードできる
                                        <br />
                                        有効期間は7日です
                                      </>
                                    }
                                  >
                                    <InfoOutlined
                                      fontSize="small"
                                      tabIndex={0}
                                      role="img"
                                      aria-label="ダウンロードできる有効期間は7日です"
                                      sx={{ color: 'action.active' }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </Stack>
                          )}
                          {canReexport && reportExport.filterCondition && (
                            <ReportExportRowMenu
                              onReexport={() => handleReexport(reportExport)}
                            />
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={reportExportsCount || 0}
            page={filters.page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={filters.pageSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="1ページあたり:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}〜${to} (${count !== -1 ? count : `不明`} 件中)`
            }
            sx={{
              '& .MuiTablePagination-selectLabel': {
                margin: 0,
              },
              '& .MuiTablePagination-displayedRows': {
                margin: 0,
              },
            }}
          />
        </TableContainer>
      </ReportExportListWrapper>
      {/* 成功通知 */}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="現在レポートを出力中です。時間をおいてから再読み込みしてください。"
      />
    </>
  )
}
